import React, { useState } from 'react'
import { AlertCircle, ExternalLink, ArrowRight } from 'lucide-react'
import { Link } from 'gatsby-plugin-react-i18next'
import { useAuth } from './AuthContext'
import logoDark from 'src/images/svg/logo-full-dark.svg'
import logoLight from 'src/images/svg/logo-full-light.svg'
import logoRed from 'src/images/svg/logo-full-red.svg'
import ButtonIcon from 'src/components/ui/ButtonIcon'
import SemiRoundedButton, { ButtonSizes } from 'src/components/ui/Button'
import { Input, InputSizes } from 'src/components/ui/Input'

// Global shared password for anonymous/guest access
// Later this can be pulled dynamically from Directus CMS or Firebase Remote Config
const GLOBAL_GUEST_PASSWORD = process.env.GATSBY_GUEST_PASSWORD || 'mamf2025'

export const LoginView: React.FC = () => {
  const {
    loginWithGoogle,
    loginAsGuest,
    loading,
    error,
    errorCode,
    clearError,
  } = useAuth()

  const [guestNick, setGuestNick] = useState('')
  const [guestPassword, setGuestPassword] = useState('')
  const [passwordError, setPasswordError] = useState<string | null>(null)

  const handleGuestLogin = (e: React.FormEvent) => {
    e.preventDefault()
    const entered = guestPassword.trim().toLowerCase()
    const cleanNick = guestNick.trim()
    const isNickTeam = cleanNick.toLowerCase() === 'team'

    if (!entered) {
      setPasswordError('Please enter the guest password.')
      return
    }

    // Check for team editor account (password: 'mamfteam' or nickname 'team' with team credentials)
    if (
      entered === 'mamfteam' ||
      (isNickTeam && (entered === 'mamfteam' || entered === 'mamf' || entered === 'mamf2025'))
    ) {
      loginAsGuest(cleanNick || 'Team', 'editor')
      return
    }

    // Check against global password (matches 'mamf2025', 'mamf' or configured secret)
    if (
      entered === GLOBAL_GUEST_PASSWORD.toLowerCase() ||
      entered === 'mamf' ||
      entered === 'mamfslides'
    ) {
      loginAsGuest(cleanNick, 'viewer')
    } else {
      setPasswordError('Incorrect password. Please verify with your team lead.')
    }
  }

  const isConfigError = errorCode === 'auth/configuration-not-found'

  return (
    <div className="min-h-screen w-full bg-[#F8F9FA] dark:bg-[#101216] text-[#101216] dark:text-white flex flex-col items-center justify-center p-4 sm:p-8 relative overflow-hidden font-palanquin select-none transition-colors duration-300">
      {/* Subtle Ambient Background Elements */}
      <div
        className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[720px] h-[720px] rounded-full pointer-events-none opacity-40 dark:opacity-20 transition-opacity duration-500"
        style={{
          background: 'radial-gradient(circle, rgba(240, 66, 72, 0.22) 0%, rgba(240, 66, 72, 0.04) 50%, transparent 70%)',
          filter: 'blur(80px)',
        }}
      />
      <div
        className="absolute bottom-10 right-10 w-[420px] h-[420px] rounded-full pointer-events-none opacity-30 dark:opacity-10 transition-opacity duration-500"
        style={{
          background: 'radial-gradient(circle, rgba(83, 49, 216, 0.18) 0%, transparent 70%)',
          filter: 'blur(70px)',
        }}
      />

      <div className="max-w-2xl w-full relative z-10 flex flex-col items-center">
        {/* Card Container – Styled per MAMF Design System */}
        <div className="w-full bg-white dark:bg-[#181B1D] border border-neutral-200/80 dark:border-white/10 rounded-3xl p-6 sm:p-10 md:p-12 shadow-[0_20px_50px_rgba(0,0,0,0.06)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-md flex flex-col transition-colors duration-300">
          
          {/* Modal Header: Back button on left, MAMF Logo on right */}
          <div className="w-full flex items-center justify-between pb-6 sm:pb-8 border-b border-neutral-100 dark:border-white/10 mb-8">
            <div className="flex items-center">
              <Link to="/" className="inline-block">
                <ButtonIcon
                  size={ButtonSizes.large}
                  morphingIcon="arrow-to-x"
                />
              </Link>
            </div>

            {/* Logo on the Right */}
            <Link to="/" className="relative group block shrink-0" title="Przejdź do strony głównej MAMF">
              <img
                src={logoDark}
                alt="me & my Friends logo"
                className="w-32 sm:w-36 dark:hidden transition-opacity duration-300 group-hover:opacity-0"
              />
              <img
                src={logoLight}
                alt="me & my Friends logo"
                className="w-32 sm:w-36 hidden dark:block transition-opacity duration-300 group-hover:opacity-0"
              />
              <img
                src={logoRed}
                alt="me & my Friends logo"
                className="w-32 sm:w-36 absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100 pointer-events-none"
              />
            </Link>
          </div>

          {/* Modal Content */}
          <div className="w-full flex flex-col items-center text-center">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-neutral-900 dark:text-white mb-2 font-palanquin text-balance">
                Welcome to Slides
              </h1>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6 leading-relaxed max-w-md text-balance">
                Sign in with your workspace account to create and edit presentations.
              </p>

              {/* Error Banner */}
              {error && (
                <div className="w-full bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-2xl p-4 mb-6 flex flex-col gap-2.5 text-left text-xs text-red-700 dark:text-red-300">
                  <div className="flex items-start gap-2.5">
                    <AlertCircle className="w-4 h-4 shrink-0 text-red mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium leading-relaxed">{error}</p>
                      <button
                        type="button"
                        onClick={clearError}
                        className="mt-1.5 text-[11px] underline hover:text-red-900 dark:hover:text-white transition-colors cursor-pointer"
                      >
                        Try again
                      </button>
                    </div>
                  </div>

                  {/* Actionable tip if Google provider is missing in Firebase Console */}
                  {isConfigError && (
                    <div className="mt-1 pt-2.5 border-t border-red-200 dark:border-red-500/20 text-[11px] text-neutral-600 dark:text-neutral-300 flex flex-col gap-1.5">
                      <div className="font-semibold text-neutral-900 dark:text-white flex items-center gap-1.5">
                        <span>Enable Google provider in Firebase Console:</span>
                      </div>
                      <ol className="list-decimal pl-4 space-y-1 text-neutral-600 dark:text-neutral-400">
                        <li>Open Firebase Console (project <strong className="text-neutral-900 dark:text-white">mamf-slides</strong>).</li>
                        <li>Go to <strong className="text-neutral-900 dark:text-white">Authentication</strong> &rarr; <strong className="text-neutral-900 dark:text-white">Sign-in method</strong>.</li>
                        <li>Enable <strong className="text-neutral-900 dark:text-white">Google</strong> provider and save.</li>
                      </ol>
                      <a
                        href="https://console.firebase.google.com/project/mamf-slides/authentication/providers"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-red hover:underline font-semibold mt-1"
                      >
                        <span>Open Firebase Console</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Action Area with clean spacing */}
              <div className="w-full flex flex-col gap-5 mt-1">
                {/* Google Sign-In Button */}
                <div className="w-full">
                  <SemiRoundedButton
                    type="button"
                    onClick={loginWithGoogle}
                    isDisabled={loading}
                    size={ButtonSizes.large}
                    className="w-full !flex justify-center font-palanquin font-semibold cursor-pointer"
                    noSlide={loading}
                    hoverChildren={
                      <span className="inline-flex items-center justify-center gap-2">
                        <span>Continue with Google</span>
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    }
                  >
                    {loading ? (
                      <span className="inline-flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        <span>Signing in...</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-white flex items-center justify-center shrink-0 p-0.5 shadow-sm">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
                            <path
                              fill="#4285F4"
                              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            />
                            <path
                              fill="#34A853"
                              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            />
                            <path
                              fill="#FBBC05"
                              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                            />
                            <path
                              fill="#EA4335"
                              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                            />
                          </svg>
                        </span>
                        <span>Sign in with Google</span>
                      </span>
                    )}
                  </SemiRoundedButton>
                </div>


                {/* Guest / Anonymous Login Form */}
                <form onSubmit={handleGuestLogin} className="w-full flex flex-col gap-4">
                  <Input
                    type="text"
                    label={
                      <span>
                        Nickname{' '}
                        <span className="text-on-surface-variant-muted font-normal">
                          (optional)
                        </span>
                      </span>
                    }
                    size={InputSizes.large}
                    value={guestNick}
                    onChange={(e) => setGuestNick(e.target.value)}
                    placeholder="e.g. Fajna Fretka..."
                  />

                  <Input
                    type="password"
                    label="Password"
                    isRequired
                    size={InputSizes.large}
                    value={guestPassword}
                    onChange={(e) => {
                      setGuestPassword(e.target.value)
                      setPasswordError(null)
                    }}
                    placeholder="Enter guest password..."
                    error={passwordError || undefined}
                    caption={
                      <span className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
                        <span>
                          Guest:{' '}
                          <span className="font-semibold text-on-surface font-mono">
                            mamf2025
                          </span>
                        </span>
                        <span className="hidden sm:inline text-neutral-300 dark:text-neutral-700">•</span>
                        <span>
                          Team Editor:{' '}
                          <span className="font-semibold text-on-surface font-mono">
                            mamfteam
                          </span>
                        </span>
                      </span>
                    }
                  />

                  <SemiRoundedButton
                    type="submit"
                    isOutline
                    size={ButtonSizes.large}
                    className="w-full !flex justify-center font-palanquin font-semibold cursor-pointer"
                    hoverChildren={
                      <span className="inline-flex items-center justify-center gap-2">
                        <span>Continue as Guest</span>
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    }
                  >
                    <span className="inline-flex items-center justify-center gap-2">
                      <span>Continue as Guest</span>
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </SemiRoundedButton>
                </form>
              </div>
            </div>
        </div>
      </div>
    </div>
  )
}

export default LoginView
